/**
 * Kolejka offline po stronie strony (PLAN.md §8, Faza 7).
 *
 * Właścicielem kolejki jest service worker: to on przechwytuje nieudane
 * `POST /api/items`, zapisuje je w IndexedDB i opróżnia kolejkę przez
 * Background Sync. Ten moduł:
 *
 *  - czyta licznik zaległych (wskaźnik w topbarze),
 *  - słucha `postMessage` z SW po każdej zmianie kolejki,
 *  - jest **awaryjną** ścieżką, gdy SW nie kontroluje strony (dev server,
 *    pierwsze wejście przed instalacją): sam dopisuje wrzutkę do tej samej
 *    bazy i sam ją wysyła po powrocie sieci.
 *
 * Schemat IndexedDB jest wspólny z `src/sw/sw.js` - obie strony trzymają się
 * tych samych nazw (celowa, niewielka duplikacja: SW jest bundlowany osobno).
 */

export const DB_NAME = "gtd-offline";
export const DB_VERSION = 1;
export const STORE = "captures";

/** Zakolejkowana wrzutka: dokładnie to, co poszłoby POST-em na /api/items. */
export interface QueuedCapture {
  id?: number;
  raw_text: string;
  source: string;
  extra?: Record<string, unknown> | null;
  queued_at: string;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE, mode);
        const request = run(transaction.objectStore(STORE));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
        transaction.oncomplete = () => db.close();
      }),
  );
}

export async function queuedCount(): Promise<number> {
  try {
    return await tx("readonly", (store) => store.count());
  } catch {
    return 0;
  }
}

export async function queuedItems(): Promise<QueuedCapture[]> {
  try {
    return await tx<QueuedCapture[]>("readonly", (store) => store.getAll());
  } catch {
    return [];
  }
}

/** Dopisuje wrzutkę do kolejki (ścieżka awaryjna, gdy nie ma SW). */
export async function enqueue(item: Omit<QueuedCapture, "id" | "queued_at">): Promise<void> {
  await tx("readwrite", (store) =>
    store.add({ ...item, queued_at: new Date().toISOString() } satisfies QueuedCapture),
  );
  notifyLocal(await queuedCount());
}

async function remove(id: number): Promise<void> {
  await tx("readwrite", (store) => store.delete(id));
}

// --------------------------------------------------------------------------
// Opróżnianie
// --------------------------------------------------------------------------

let flushing = false;

/**
 * Statusy, po których wrzutka zostaje w kolejce (patrz `src/sw/sw.js` - obie
 * implementacje muszą się zgadzać). 401/403 to wygasłe ciasteczko: wrzutki nie
 * wolno wtedy skasować, bo nigdy nie dotarła do serwera.
 */
export function shouldRequeue(status: number): boolean {
  return status >= 500 || status === 401 || status === 403 || status === 408 || status === 429;
}

/**
 * Próbuje wysłać zaległe wrzutki.
 *
 * Gdy stroną steruje service worker, robotę oddajemy jemu (jedno miejsce =
 * brak wyścigu o te same rekordy). Bez SW wysyłamy sami.
 */
export async function flushQueue(): Promise<number> {
  const controller = navigator.serviceWorker?.controller;
  if (controller) {
    controller.postMessage({ type: "queue.flush" });
    return 0;
  }

  if (flushing) return 0;
  flushing = true;
  let sent = 0;
  try {
    for (const item of await queuedItems()) {
      if (item.id === undefined) continue;
      let resp: Response;
      try {
        resp = await fetch("/api/items", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            raw_text: item.raw_text,
            source: item.source,
            extra: item.extra ?? null,
          }),
        });
      } catch {
        break; // sieci dalej nie ma - rekord zostaje, reszta poczeka
      }
      if (!resp.ok && shouldRequeue(resp.status)) break; // np. 401 - dosyłamy po zalogowaniu
      // Sukces albo trwały błąd wejścia (400/422) - rekord znika z kolejki.
      await remove(item.id);
      if (resp.ok) sent += 1;
    }
  } finally {
    flushing = false;
    notifyLocal(await queuedCount());
  }
  return sent;
}

// --------------------------------------------------------------------------
// Subskrypcja licznika
// --------------------------------------------------------------------------

type CountListener = (count: number) => void;

const listeners = new Set<CountListener>();
let lastCount = 0;
let wired = false;

function notifyLocal(count: number): void {
  lastCount = count;
  for (const listener of listeners) listener(count);
}

function wire(): void {
  if (wired) return;
  wired = true;

  navigator.serviceWorker?.addEventListener("message", (event: MessageEvent) => {
    const data = event.data as { type?: string; count?: number } | undefined;
    if (data?.type === "queue.changed" && typeof data.count === "number") notifyLocal(data.count);
  });

  // Powrót sieci i start appki to dwa momenty, w których warto spróbować
  // ponownie - Background Sync bywa niedostępny (np. Firefox).
  window.addEventListener("online", () => void flushQueue());
  void queuedCount().then((count) => {
    notifyLocal(count);
    if (count > 0) void flushQueue();
  });
}

export function subscribeQueueCount(listener: CountListener): () => void {
  wire();
  listeners.add(listener);
  listener(lastCount);
  void queuedCount().then(notifyLocal);
  return () => listeners.delete(listener);
}

/** Wywoływane raz przy starcie appki (main.tsx). */
export function initOfflineQueue(): void {
  wire();
}
